import { inject, Signal } from '@angular/core';

import {
  QueryClient,
  injectMutation,
  injectQuery,
} from '@tanstack/angular-query-experimental';

import { combineLatest, firstValueFrom, take } from 'rxjs';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@services/toast.service';
import { FeedAlgorithm } from 'src/app/core/models/algorithm.models';
import { NomObjectTypes } from 'src/app/core/constants';
import {
  ContentFilters,
  FeedRemovedItem,
  Post,
} from 'src/app/core/models/core.models';
import { removeSelectedPostsFromFeedSuccess } from 'src/app/core/actions/feed.actions';
import {
  getContentFilters,
  selectCurrentObject,
  selectFeed,
  selectFeedFilters,
} from 'src/app/core/reducers';
import { FeedFiltersService } from 'src/app/core/services/feed-filters.service';
import { buildPostThreadMap } from 'src/app/shared/utils/post-thread.utils';
import {
  allWidgetDataQueriesFilter,
  objectWidgetsQueriesFilter,
} from './widget.queries';

import {
  CopyFeedParams,
  FeedApiService,
  FeedPostViewMode,
  QueryValidationError,
  QueryValidationResponse,
  QueryValidationSuccess,
} from '../services/feed-api.service';
import { NomenclatureApiService } from '../services/nomenclature-api.service';
import { UserApiService } from '../services/user-api.service';

/**
 * Query-language operator hints for an object type (GET /feed/feed_operator_hints).
 *
 * Reference data, cached generously: it only changes with a backend release.
 * `FeedService.operatorsCache` is a second cache of the same data, kept for two
 * service-to-service callers that cannot hold a hook.
 */
export function injectFeedOperatorHintsQuery(
  objectTypeId: Signal<number | null>,
) {
  const feedApi = inject(FeedApiService);

  return injectQuery(() => ({
    queryKey: ['feed', 'feed_operator_hints', objectTypeId()],
    queryFn: () => firstValueFrom(feedApi.getFeedOperatorHints(objectTypeId())),
    enabled: objectTypeId() != null,
    staleTime: 30 * 60 * 1000,
  }));
}

/**
 * Company categories assigned to a feed (GET /feed/:id/categories) — the
 * category picker in the query editor.
 */
export function injectFeedUserCategoriesQuery(feedId: Signal<string | null>) {
  const feedApi = inject(FeedApiService);

  return injectQuery(() => ({
    queryKey: ['feed', 'categories', feedId()],
    queryFn: () => firstValueFrom(feedApi.getUserCategories(feedId())),
    enabled: !!feedId(),
  }));
}

/**
 * Workflow definitions (GET /nom/workflows) — reference data for the feed's
 * workflow picker.
 *
 * Lives here, by consumer domain, rather than with the other `nom` reads; the
 * api-service it calls still follows the URL root.
 */
export function injectWorkflowsQuery() {
  const nomenclatureApi = inject(NomenclatureApiService);

  return injectQuery(() => ({
    queryKey: ['nom', 'workflows'],
    queryFn: () => firstValueFrom(nomenclatureApi.getWorkflows()),
    staleTime: 30 * 60 * 1000,
  }));
}

// ! ─── Feed algorithms ──────────────────────────────────────────────────────

export const feedAlgorithmsQueryKey = (feedId: string | null) =>
  ['feed', 'algorithms', feedId] as const;

/**
 * All algorithms applicable to a feed, each flagged `is_selected`
 * (GET /feed/:id/algorithms).
 *
 * Cache-as-state: detach patches this entry rather than refetching it.
 */
export function injectFeedAlgorithmsQuery(feedId: Signal<string | null>) {
  const feedApi = inject(FeedApiService);

  return injectQuery(() => ({
    queryKey: feedAlgorithmsQueryKey(feedId()),
    queryFn: () =>
      firstValueFrom(feedApi.getFeedAlgorithms(feedId()!)).then(
        (algorithms) => algorithms ?? [],
      ),
    enabled: !!feedId(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  }));
}

// ! There is no attach mutation: an algorithm is attached by **scheduling** it
// ! from the Actions menu, which the apply request below does server-side
// ! (WAP-1379). The old attach endpoint is being deprecated.

export interface DetachFeedAlgorithmInput {
  feedId: string;
  algorithmId: number;
  scheduleId?: string | null;
}

/** **No refetch, deliberately** — flipping `is_selected` is the whole update. */
export function injectDetachFeedAlgorithmMutation() {
  const feedApi = inject(FeedApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({
      feedId,
      algorithmId,
      scheduleId,
    }: DetachFeedAlgorithmInput) =>
      firstValueFrom(
        feedApi.detachFeedAlgorithm(feedId, algorithmId, scheduleId),
      ),
    onSuccess: (_result, { feedId, algorithmId }) => {
      queryClient.setQueryData<FeedAlgorithm[]>(
        feedAlgorithmsQueryKey(feedId),
        (current) =>
          current?.map((algorithm) =>
            algorithm.id === algorithmId
              ? { ...algorithm, is_selected: false }
              : algorithm,
          ),
      );
      toast.showSuccess(
        translate.instant(
          'feed-algorithms.message.algorithm-detached-successfully',
        ),
      );
    },
    onError: () => {
      toast.showError(
        translate.instant('feed-algorithms.message.detach-failed'),
      );
    },
  }));
}

export interface UpdateFeedAlgorithmParamsInput {
  feedId: string;
  algorithmId: number;
  params: Record<string, any>;
  scheduleId?: string | null;
}

/**
 * Invalidates rather than patching: the edited params come back in the list
 * GET's response, not in this PATCH's.
 */
export function injectUpdateFeedAlgorithmParamsMutation() {
  const feedApi = inject(FeedApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({
      feedId,
      algorithmId,
      params,
      scheduleId,
    }: UpdateFeedAlgorithmParamsInput) =>
      firstValueFrom(
        feedApi.updateFeedAlgorithmParams(
          feedId,
          algorithmId,
          params,
          scheduleId,
        ),
      ),
    onSuccess: (_result, { feedId }) => {
      queryClient.invalidateQueries({
        queryKey: feedAlgorithmsQueryKey(feedId),
      });
      toast.showSuccess(
        translate.instant(
          'feed-algorithms.message.algorithm-settings-updated-successfully',
        ),
      );
    },
    onError: () => {
      toast.showError(
        translate.instant('feed-algorithms.message.update-params-failed'),
      );
    },
  }));
}

export interface ApplyFeedPostAlgorithmInput {
  feedId: string;
  algorithmId: number;
  contentFilters: ContentFilters;
  algorithmParams: Record<string, any>;
}

/**
 * Run an algorithm over a feed's whole result set. It touches no list: the
 * response is the notification id tracking the run, and progress arrives
 * through the notifications stream. Several runs may be in flight at once.
 */
export function injectApplyFeedPostAlgorithmMutation() {
  const feedApi = inject(FeedApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({
      feedId,
      algorithmId,
      contentFilters,
      algorithmParams,
    }: ApplyFeedPostAlgorithmInput) =>
      firstValueFrom(
        feedApi.applyFeedPostsAlgorithm(
          feedId,
          algorithmId,
          contentFilters,
          algorithmParams,
        ),
      ),
    onSuccess: (_result, { algorithmParams }) => {
      // * Scheduling and running once are the same request, so the toast has to
      // * branch on `schedule` to avoid claiming the wrong thing happened.
      toast.showSuccess(
        translate.instant(
          algorithmParams?.['schedule']
            ? 'feed.toasts.algorithm-scheduled-successfully'
            : 'feed.toasts.algorithm-applied-successfully',
        ),
      );
    },
  }));
}

// ! ─── Removed ("excluded") items ───────────────────────────────────────────
// ! One consumer: the Excluded Posts tab of the feed's search-builder pane.
// ! Mounting the panel is the fetch.

const REMOVED_ITEMS_ROOT_KEY = ['feed', 'removed-items'] as const;

export const feedRemovedItemsQueryKey = (feedId: string | null) =>
  [...REMOVED_ITEMS_ROOT_KEY, feedId] as const;

/** Every mounted removed-items list. See `patchRemovedItemLists`. */
const allRemovedItemListsFilter = { queryKey: REMOVED_ITEMS_ROOT_KEY };

/**
 * A feed's removed items (GET /feed/:id/removed_items).
 *
 * `staleTime: 0` so re-opening the tab refetches. Cache-as-state, so no
 * background refetch may land on top of a mutation's patch.
 */
export function injectFeedRemovedItemsQuery(feedId: Signal<string | null>) {
  const feedApi = inject(FeedApiService);

  return injectQuery(() => ({
    queryKey: feedRemovedItemsQueryKey(feedId()),
    queryFn: () =>
      firstValueFrom(feedApi.getRemovedItems(feedId()!)).then(
        (items) => items ?? [],
      ),
    enabled: !!feedId(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  }));
}

export interface RemovePostFromFeedInput {
  feedId: string;
  postId: string;
}

/**
 * The follow-up every feed-content **write** owes: the server would now answer
 * differently on identical request params, so nothing refetches by key change.
 * Reloads the posts list, the widget data, and every already-expanded filter
 * (their item counts are narrowed by what just changed; a filter still at
 * `offset === -1` was never expanded).
 *
 * One implementation for every owner - the removal mutations below hold it
 * directly, `FeedDimensionsService` and `FeedPersistenceService` hold it as an
 * imperative factory.
 */
export function injectFeedWriteRefresh() {
  const queryClient = inject(QueryClient);
  const feedFilters = inject(FeedFiltersService);
  const store = inject(Store);

  return (): void => {
    combineLatest([
      store.select(selectCurrentObject),
      store.select(selectFeed),
      store.select(selectFeedFilters),
      store.select(getContentFilters),
    ])
      .pipe(take(1))
      .subscribe(([object, feed, filters, contentFilters]) => {
        if (feed) {
          queryClient.invalidateQueries(feedPostsListsFilter(feed.id));
        }

        queryClient.invalidateQueries(allWidgetDataQueriesFilter());
        // * widget cards show datasource / dimension titles from the widget rows
        queryClient.invalidateQueries(objectWidgetsQueriesFilter());

        if (object) {
          filters.forEach((filter) => {
            if (filter.offset !== -1) {
              feedFilters.loadFilterItems(object.id, filter, contentFilters, 0);
            }
          });
        }
      });
  };
}

/** The post card's "remove from feed". The hook owns the content refresh. */
export function injectRemovePostFromFeedMutation() {
  const feedApi = inject(FeedApiService);
  const queryClient = inject(QueryClient);
  const feedWriteRefresh = injectFeedWriteRefresh();
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({ feedId, postId }: RemovePostFromFeedInput) =>
      firstValueFrom(feedApi.removePostFromFeed(feedId, postId)),
    onSuccess: (removedItems) => {
      mergeRemovedItems(queryClient, removedItems);
      feedWriteRefresh();
      toast.showSuccess(
        translate.instant('feed.toasts.post-removed-successfully'),
      );
    },
    onError: () => {
      toast.showError(translate.instant('feed.toasts.post-remove-failed'));
    },
  }));
}

export interface RemoveItemsBySourceInput {
  feedId: string;
  objectIds: string[];
  objectClassId: number;
}

/**
 * The post card's "remove all posts from this source". Silent on purpose: no
 * toast on either branch.
 */
export function injectRemoveItemsBySourceMutation() {
  const feedApi = inject(FeedApiService);
  const queryClient = inject(QueryClient);
  const feedWriteRefresh = injectFeedWriteRefresh();

  return injectMutation(() => ({
    mutationFn: ({
      feedId,
      objectIds,
      objectClassId,
    }: RemoveItemsBySourceInput) =>
      firstValueFrom(
        feedApi.removeItemsBySource(feedId, objectIds, objectClassId),
      ),
    onSuccess: (removedItems) => {
      mergeRemovedItems(queryClient, removedItems);
      feedWriteRefresh();
    },
  }));
}

export interface RemoveSelectedPostsFromFeedInput {
  feedId: string;
  applyToChildObjects: boolean;
}

/**
 * The results toolbar's "remove selection".
 *
 * The only one that **replaces** the list instead of merging into it: the
 * response is the feed's whole removed list, not a delta. The hook owns the
 * content refresh and the `removeSelectedPostsFromFeedSuccess` broadcast, whose
 * reducer handler zeroes `feed.count_selected_posts`.
 */
export function injectRemoveSelectedPostsFromFeedMutation() {
  const feedApi = inject(FeedApiService);
  const queryClient = inject(QueryClient);
  const feedWriteRefresh = injectFeedWriteRefresh();
  const store = inject(Store);

  return injectMutation(() => ({
    mutationFn: ({
      feedId,
      applyToChildObjects,
    }: RemoveSelectedPostsFromFeedInput) =>
      firstValueFrom(
        feedApi.removeSelectedPostsFromFeed(feedId, applyToChildObjects),
      ),
    onSuccess: (removedItems) => {
      patchRemovedItemLists(queryClient, () => removedItems ?? []);
      feedWriteRefresh();
      store.dispatch(removeSelectedPostsFromFeedSuccess());
    },
  }));
}

export interface RestoreRemovedItemInput {
  feedId: string;
  objectId: string;
  objectClassId: number;
}

/**
 * Restore one item from the panel, matched on `object_id` alone. The hook owns
 * the content refresh.
 */
export function injectRestoreRemovedItemMutation() {
  const feedApi = inject(FeedApiService);
  const queryClient = inject(QueryClient);
  const feedWriteRefresh = injectFeedWriteRefresh();

  return injectMutation(() => ({
    mutationFn: ({
      feedId,
      objectId,
      objectClassId,
    }: RestoreRemovedItemInput) =>
      firstValueFrom(
        feedApi.restoreRemovedItem(feedId, objectId, objectClassId),
      ),
    onSuccess: (_result, { objectId }) => {
      patchRemovedItemLists(queryClient, (items) =>
        items.filter((item) => item.object_id !== objectId),
      );
      feedWriteRefresh();
    },
  }));
}

/** The panel's "Restore all". The hook owns the content refresh. */
export function injectRestoreAllRemovedItemsMutation() {
  const feedApi = inject(FeedApiService);
  const queryClient = inject(QueryClient);
  const feedWriteRefresh = injectFeedWriteRefresh();

  return injectMutation(() => ({
    mutationFn: (feedId: string) =>
      firstValueFrom(feedApi.restoreAllRemovedItems(feedId)),
    onSuccess: () => {
      patchRemovedItemLists(queryClient, () => []);
      feedWriteRefresh();
    },
  }));
}

/**
 * Append what the response carries that the list does not already hold, keyed
 * on `object_id` only.
 */
const mergeRemovedItems = (
  queryClient: QueryClient,
  removedItems: FeedRemovedItem[] | null,
): void => {
  if (!removedItems?.length) return;

  patchRemovedItemLists(queryClient, (items) => {
    const known = new Set(items.map((item) => item.object_id));

    return [
      ...items,
      ...removedItems.filter((item) => !known.has(item.object_id)),
    ];
  });
};

/**
 * Apply a patch to every cached removed-items list.
 *
 * Not keyed, on purpose: the call sites draw their feed id from three different
 * places, and only one list is ever mounted (the panel is a tab of one feed's
 * pane). Targeting by key would buy nothing but a way for those id sources to
 * drift apart.
 */
const patchRemovedItemLists = (
  queryClient: QueryClient,
  patch: (items: FeedRemovedItem[]) => FeedRemovedItem[],
): void => {
  queryClient.setQueriesData<FeedRemovedItem[]>(
    allRemovedItemListsFilter,
    (current) => (current ? patch(current) : current),
  );
};

// ! ─── The small feed writes ────────────────────────────────────────────────
// ! Fire-and-forget PATCHes with no read of their own. Neither answers with
// ! anything the UI uses: the reducer patches the feed off the caller's
// ! broadcast, or has already patched it before the request is built.

/**
 * The results toolbar's "clear selection".
 *
 * No toast and no cache work: the visible change is the caller dispatching
 * `clearFeedSelectedItems`, which deselects every loaded post.
 */
export function injectClearFeedSelectionMutation() {
  const userApi = inject(UserApiService);

  return injectMutation(() => ({
    mutationFn: ({ userId, feedId }: { userId: string; feedId: string }) =>
      firstValueFrom(userApi.clearFeedSelectedItems(userId, feedId)),
  }));
}

/**
 * The post-list layout picker. The caller broadcasts
 * `setFeedPostViewModeSuccess`, which is what patches `feed.view_posts_mode` -
 * the feed object is reducer state, not a cache entry.
 */
export function injectSetFeedPostViewModeMutation() {
  const feedApi = inject(FeedApiService);

  return injectMutation(() => ({
    mutationFn: ({
      feedId,
      viewMode,
    }: {
      feedId: string;
      viewMode: FeedPostViewMode;
    }) => firstValueFrom(feedApi.setFeedPostViewMode(feedId, viewMode)),
  }));
}

// ! ─── Feed create / edit / duplicate ───────────────────────────────────────
// ! Nothing here reads or patches a query cache: the feed object is reducer
// ! state, so every consumer is fed by the `…Success` action the call site
// ! broadcasts, and there are a dozen of them across three slices and several
// ! `ofType` listeners. **Those broadcasts are the contract**; the hooks own only
// ! the requests and the toasts.

/**
 * The query parser rejected the query — a **200 with an error body**, not an HTTP
 * failure. Thrown so the call site can report it with `message` + `line` +
 * **`charPos`**.
 */
export class FeedValidationFailed extends Error {
  constructor(readonly validation: QueryValidationError) {
    super(validation.message);
    this.name = 'FeedValidationFailed';
  }
}

/**
 * The validate *request* itself failed. A separate class because it is reported
 * differently from the case above — `message` off `error.response.message` and
 * **`column`**, not `charPos` — and because anything else reaching the call
 * site's `catch` means the save failed, not the validation.
 */
export class FeedValidationRequestFailed extends Error {
  constructor(readonly httpError: any) {
    super('Feed query validation request failed');
    this.name = 'FeedValidationRequestFailed';
  }
}

/** validate → save, shared by create and edit. */
const validateThenSave = async <T>(
  validate: () => Promise<QueryValidationResponse>,
  save: () => Promise<T>,
): Promise<T> => {
  let validation: QueryValidationResponse;

  try {
    validation = await validate();
  } catch (httpError) {
    throw new FeedValidationRequestFailed(httpError);
  }

  if (!isValidationSuccess(validation)) {
    throw new FeedValidationFailed(validation);
  }

  return save();
};

const isValidationSuccess = (
  result: QueryValidationResponse,
): result is QueryValidationSuccess =>
  (result as QueryValidationSuccess).success === true;

export interface SaveFeedInput {
  feedData: any;
}

/**
 * Validate the query, then PUT `/feed`.
 *
 * No toast here: `feedCreateSuccess$` owns it, because that effect also
 * **navigates** to the new feed and navigation stays NgRx. It fires off the
 * `createFeedSuccess` the caller broadcasts.
 */
export function injectCreateFeedMutation() {
  const feedApi = inject(FeedApiService);

  return injectMutation(() => ({
    mutationFn: ({ feedData }: SaveFeedInput) =>
      validateThenSave(
        () =>
          firstValueFrom(
            feedApi.validateQuery(
              feedData.object_type_id,
              feedData.query_text,
              feedData.query_created_from,
            ),
          ),
        () => firstValueFrom(feedApi.createFeed(feedData)),
      ),
  }));
}

export interface EditFeedInput extends SaveFeedInput {
  feedId: string;
}

/**
 * Validate (passing the feed id, which the create path cannot), then PATCH
 * `/feed/:id`.
 *
 * No category-tree reload needed: `post-category-tree`'s query key is derived
 * from the feed's category ids, so the edited feed landing in the store
 * refetches it when they change.
 */
export function injectEditFeedMutation() {
  const feedApi = inject(FeedApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({ feedId, feedData }: EditFeedInput) =>
      validateThenSave(
        () =>
          firstValueFrom(
            feedApi.validateQuery(
              feedData.object_type_id,
              feedData.query_text,
              feedData.query_created_from,
              feedId,
            ),
          ),
        () => firstValueFrom(feedApi.editFeed(feedId, feedData)),
      ),
    onSuccess: (feed) => {
      toast.showSuccess(
        translate.instant(
          feed.object_type_id != NomObjectTypes.SEARCH_DIMENSION
            ? 'feed.toasts.feed-edited-successfully'
            : 'feed.toasts.dimension-edited-successfully',
        ),
      );
    },
  }));
}

/**
 * Copy one or several feeds.
 *
 * The endpoint answers **200 with an `error` body** when a business constraint
 * blocks the copy, so the response is a branch, not a rejection: the caller
 * (`FeedPersistenceService.duplicate`) reads it and either patches the cached
 * lists (+ `deselectObjects`) or dispatches `duplicateFeedsConstraintError`,
 * whose effect opens the info dialog.
 *
 * The toast lives here so that it still fires after `feed-duplicate-dialog`
 * closes itself, which it does the moment the request starts.
 */
export function injectDuplicateFeedsMutation() {
  const feedApi = inject(FeedApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (copyFeedParams: CopyFeedParams) =>
      firstValueFrom(feedApi.duplicateFeeds(copyFeedParams)),
    onSuccess: (response) => {
      if ('error' in response) return;

      toast.showSuccess(
        translate.instant('feed.toasts.feed-duplicated-successfully'),
      );
    },
  }));
}

// ! ─── The posts list ───────────────────────────────────────────────────────
// ! **The cached value is not the response.** `buildPostThreadMap` turns the
// ! server's flat array into the rendered entry map, in which the feed-matching
// ! post is the canonical entry and each appearance of it as an ancestor inside
// ! another thread is a display-only `${realId}__shadow__${leafId}` copy. Doing
// ! it in the queryFn keeps **one** representation: mutation patches, selection
// ! flags and collapse flags all write the structure the list renders.
// !
// ! The key carries the whole of `getContentFilters`, unlike `widgetDataFilterKey`
// ! which strips the paging terms: for the post list, paging *is* the resource
// ! identity, so a page or sort change reloads by changing the key.

export interface FeedPostsScope {
  feedId: string;
  contentFilters: ContentFilters;
}

/** What the list query caches: the rendered entry map plus the server's total. */
export interface FeedPostsData {
  posts: Map<string, Post>;
  totalItemsCount: number;
}

const FEED_POSTS_ROOT_KEY = ['feed', 'posts'] as const;

export const feedPostsQueryKey = (scope: FeedPostsScope | null) =>
  [
    ...FEED_POSTS_ROOT_KEY,
    scope?.feedId ?? null,
    scope?.contentFilters ?? null,
  ] as const;

/**
 * Every cached page of one feed's list. Server-truth patches target this rather
 * than a recomputed exact key: a mutation can resolve after the user has paged,
 * and a post id absent from a given page makes that entry a no-op - whereas a
 * key rebuilt from current state would write the wrong page.
 */
export const feedPostsListsFilter = (feedId: string) => ({
  queryKey: [...FEED_POSTS_ROOT_KEY, feedId],
});

/**
 * POST /feed/:id/results — the feed's post list.
 *
 * `staleTime: 0` with no focus/reconnect refetch: the list reloads on a key
 * change (paging, sorting, filters, period, quick search, "show selected only")
 * or on an explicit invalidation, and on nothing else.
 */
export function injectFeedPostsQuery(scope: Signal<FeedPostsScope | null>) {
  const feedApi = inject(FeedApiService);

  return injectQuery(() => ({
    queryKey: feedPostsQueryKey(scope()),
    queryFn: async (): Promise<FeedPostsData> => {
      const { feedId, contentFilters } = scope()!;
      const response = await firstValueFrom(
        feedApi.getFeedPosts(feedId, contentFilters),
      );

      return {
        posts: buildPostThreadMap(response?.posts),
        totalItemsCount: response?.count_posts ?? 0,
      };
    },
    enabled: !!scope()?.feedId,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  }));
}
